import type { BoolNumeric } from 'types'

import type { IValueLabel } from 'common/interfaces/IValueLabel'

import type { PatientStatus } from 'features/Home/interfaces/IInfoSchedule'

import type { DrawingElement, EGender, ProcedureTypeCode } from './procedure.type'

export type EmrCodesDto = {
  siteId: string
  search: string
  code_type: ProcedureTypeCode
  category?: string
}

export type EmrCodeItem = {
  active: BoolNumeric
  code: string
  code_text: string
  code_text_short: string
  code_type: ProcedureTypeCode
  id: number
  default_price?: number
  upc?: string
}

export type EmrCodesResponse = {
  data: EmrCodeItem[]
  total: number
  limit: number
  offset: number
  success: boolean
}

export const EMR_CODE_FIELD_TYPES = [
  'string',
  'integer',
  'number',
  'dropdown',
  'multiselect',
  'checkboxgroup',
  'radiogroup',
] as const

export type EmrCodeFieldType = (typeof EMR_CODE_FIELD_TYPES)[number]

export type EmrCodeFieldBindings = string[] | Record<string, string>

export type EmrCodeDynamicField = {
  name?: string
  type: EmrCodeFieldType
  label: string
  required: boolean
  bindings?: EmrCodeFieldBindings
  col?: string
  width?: string
}

export type EmrCodeFieldRow = {
  elements: EmrCodeDynamicField[]
}

export type EmrCodeFields = EmrCodeDynamicField[] | EmrCodeFieldRow[]

export type DynamicFieldValue = string | number | string[] | undefined

export type DynamicFieldValues = Record<string, DynamicFieldValue>

export type DynamicFieldKeyValue = Record<string, string>

export type DynamicFieldsDto = Record<
  string,
  string | number | string[] | DynamicFieldKeyValue[] | null
>

export type EmrCodeDetails = {
  id: number
  code: string
  icd_items: EmrCodeItem[]
  fields: EmrCodeFields
}

export type EmrCodeDetailsResponse = {
  success: boolean
  data: EmrCodeDetails
}

export type FetchEmrCodeDetailsDto = {
  siteId: string
  codeId: number
}

export type EmrScanProductDto = {
  siteId: string
  search: string
}

export type EmrScanProductSuccess = {
  ok: true
  source: 'lot' | 'upc' | 'code' | string
  availability: 'in_stock' | 'consumed' | 'lot_unknown' | null
  id: number
  code: string
  code_text: string | null
  code_text_short: string | null
  code_type: string | null
  active: BoolNumeric | null
  default_price: number | null
  upc: string | null
  ean: string | null
  inventory_system_id: string | null
  qr_code: string | null
  retail: BoolNumeric | null
  portal: BoolNumeric | null
  unrestricted: BoolNumeric | null
  rx: BoolNumeric | null
  ob_product_code: string | null
  ob_product_id: string | null
  product_name: string | null
  ob_inventory_item_id: string | null
  lot_number: string | null
  expiration_date: string | null
  qty_on_hand: number | null
  is_serialized: boolean
}

export type EmrScanProductError = {
  ok: false
  error: string
  hint?: string
  q?: string
  source?: string
  availability?: string | null
  lot_number?: string | null
  inventory_system_id?: string | null
  ob_product_code?: string | null
  ob_product_id?: string | null
  product_name?: string | null
  ob_inventory_item_id?: string | null
  expiration_date?: string | null
  qty_on_hand?: number | null
  is_serialized?: boolean
}

export type EmrScanProductResponse = EmrScanProductSuccess | EmrScanProductError

export type PrescriptionApiItem = {
  id: number
  uuid: string | null
  patient_id: number
  provider_id: number
  encounter: number | null
  date_added: string
  date_modified: string
  start_date: string
  end_date: string | null
  drug: string
  drug_id: number
  rxnorm_drugcode: string | null
  dosage: string
  quantity: string
  size: string
  refills: number | null
  note: string | null
  active: number
  patient?: {
    fname: string
    lname: string
    DOB: string | null
  }
  provider?: {
    id: number
    fname: string
    lname: string
  }
}

export type PrescriptionsApiResponse = {
  statusCode: number
  success: boolean
  prescriptions: PrescriptionApiItem[]
  units: IValueLabel[]
  forms: IValueLabel[]
  routes: IValueLabel[]
  intervals: IValueLabel[]
}

export type DrawingsApiItem = {
  id: number
  index: number | null
  updated_at: string
  created_at: string
  drawingElements: DrawingElement[]
}

export type DocTemplate = {
  has_doc_to_sign: BoolNumeric
  id?: number
  title?: string
  type?: string
  document_id?: number | null
}

export type DiscountCategoryOptionId =
  | 'disc_cat_only_procedures'
  | 'disc_cat_only_products'
  | 'disc_cat_prod_proc'

export type DiscountCategoryOption = {
  option_id: DiscountCategoryOptionId
  title: string
  seq?: number
  notes?: string
  activity?: number
  is_default?: BoolNumeric
}

export type DiscountTypeOption = {
  option_id: string
  title: string
  seq?: number
  notes?: string
  activity?: number
  is_default?: BoolNumeric
}

export type PatientDefaultDiscount = {
  discount_category: Pick<DiscountCategoryOption, 'option_id' | 'title'> | null
  discount_type: Pick<DiscountTypeOption, 'option_id' | 'title'> | null
}

export type CheckoutPatientDefaultDiscount = {
  discount_category: Pick<DiscountCategoryOption, 'option_id' | 'title'> | null
  discount_type_products: Pick<DiscountTypeOption, 'option_id' | 'title'> | null
  discount_type_procedures: Pick<DiscountTypeOption, 'option_id' | 'title'> | null
}

export type DiscountFormContext = 'treatments' | 'products'

export type ProcedureItem = {
  id: number
  code_id?: number | null
  code_type: ProcedureTypeCode
  code: string
  code_text: string
  procedure_units: string
  units: number
  fee: number
  total_price: number
  authorized: boolean
  provider_id: number
  notecodes: string
  billed: BoolNumeric
  pricelevel: string
  revenue_code: string
  justify_codes: string[]
  drawings: DrawingsApiItem
  product_labels: string[]
  // one value set per configuration (e.g. Sofwave face + chest)
  dynamic_fields?: HistoricalDynamicFields[]
  area?: string
  energy?: string
  pulse_per_section?: string
  total_energy?: string
  note?: string
  doc_template?: DocTemplate
  discount_amount?: number
  discount_name?: string
  discount_type?: string
}

// option-type fields (radio/dropdown/multiselect/checkbox) arrive as [{key: label}]
// entries or as plain label strings, depending on the template's bindings
export type HistoricalDynamicFieldValue =
  | string
  | number
  | null
  | Array<string | Record<string, string>>

export type HistoricalDynamicFields = Record<string, HistoricalDynamicFieldValue>

export type HistoricalDataItem = {
  id: number
  code_id?: number | null
  code_type: ProcedureTypeCode
  code: string
  code_text: string
  // new API sends an array of value sets; older encounters may still be a single record
  dynamic_fields?: HistoricalDynamicFields | HistoricalDynamicFields[]
  modifier: string
  units: number
  fee: number
  total_price: number
  authorized: number
  provider_id: number
  notecodes: string
  billed: number
  pricelevel: string
  revenue_code: string
  category: string
  procedure_units: string
  product_labels: string
  justify_codes: string[]
  discount_amount?: number
  discount_name?: string
  discount_type?: string
}

export type HistoricalDataEncounter = {
  encounter_id: number
  encounter_uuid: string
  encounter_date: string
  products_count: number
  general_note: string | null
  items: HistoricalDataItem[]
}

export type HistoricalData = {
  encounter_count: number
  items: HistoricalDataEncounter[]
}

export type ProductsResponse = {
  data: {
    encounter_uuid: string
    patient_pid: number
    items: ProcedureItem[]
  }
  historical_data: HistoricalData
  model_version: string | null
  discount_categories?: DiscountCategoryOption[]
  discount_type?: DiscountTypeOption[]
  patient_default_discount?: PatientDefaultDiscount | null
}

export type ProceduresResponse = {
  encounter_uuid: string
  items: ProcedureItem[]
  model_version: string | null
  general_note: string | null
  patient_pid?: number
  discount_categories?: DiscountCategoryOption[]
  discount_type?: DiscountTypeOption[]
  patient_default_discount?: PatientDefaultDiscount | null
  historical_procedures: HistoricalData & {
    historical_documents: HistoricalDocument[]
  }
}

export type ProcedureDto = {
  id?: number
  code?: string
  code_type?: ProcedureTypeCode
  // HistoricalDynamicFields covers items echoed back unchanged (e.g. drawing saves)
  dynamic_fields?: DynamicFieldsDto[] | HistoricalDynamicFields[]
  procedure_units: string | undefined
  units: number
  fee: number
  provider_id: number
  justify_codes: string[]
  product_labels?: string[]
  drawings?: DrawingElement[]
  area?: string
  energy?: string
  pulse_per_section?: string
  total_energy?: string
  image?: string
  note?: string
  discount_type?: string
  discount_name?: string
  discount_amount?: number
}

export type FetchProceduresDto = {
  siteId: string
  encounterUid: string
}

export type FetchProductsDto = {
  siteId: string
  encounterUid: string
}

export type CreateProcedureDto = {
  siteId: string
  encounterUid: string
  data: {
    procedures: ProcedureDto[]
    model_version?: string
    gender?: EGender
  }
}

export type UpdateProcedureDto = {
  siteId: string
  encounterUid: string
  skipESignInvalidation?: boolean
  data: {
    procedures: ProcedureDto[]
    model_version?: string
    gender?: EGender
    general_note?: string
  }
}

export type UpdateModelVersionDto = {
  siteId: string
  encounterUid: string
  data: {
    model_version: string
    gender?: EGender
  }
}

export type CreateProductDto = {
  siteId: string
  encounterUid: string
  data: {
    procedures: ProcedureDto[]
  }
}

export type UpdateProductDto = {
  siteId: string
  encounterUid: string
  data: {
    procedures: ProcedureDto[]
  }
}

export type PatientEncounter = {
  encounter_uuid: string
  encounter_title: string
  encounter_date: string
  encounter_date_formatted: string
  provider: {
    id: number
    full_name: string
  } | null
}

export type Patient = {
  patient_id: number
  appointment_id?: number | null
  emr_appointment_id?: number | null
  first_name: string | null
  last_name: string | null
  consultation_staff?: number | null
  consultation_staff_name?: string | null
  age: number | null
  dob: string
  phone: string
  email?: string | null
  sex: string
  address: string
  city: string
  state: string
  zip_code: string
  country: string
  billing_note: string
  general_note: string
  referral_source?: string | null
  discount_category?: string | null
  discount_type_products?: string | null
  discount_type_procedures?: string | null
  appointment_start_time?: string | null
  appointment_time?: string | null
  appointment_time_interval?: string | null
  start_time?: string | null
  status?: {
    code: number
    name: string
  } | null
  status_code?: number | null
}

export type PatientWithEncounters = {
  patient: Patient
  encounters: PatientEncounter[]
}

export type PatientContactPayload = {
  dob: string
  address: string
  city: string
  zip_code: string
  state: string
  country: string
  billing_note: string
  general_note: string
  email: string
  phone: string
  referral_source: string
  discount_category: string | null
  discount_type_products: string | null
  discount_type_procedures: string | null
}

export type UpdatePatientContactDto = {
  siteId: string
  patientId: number
  data: PatientContactPayload
}

export type UpdatePatientContactResponse = {
  statusCode?: number
  success?: boolean
  message?: string
  data?: unknown
}

export type PhysicianIntroduction = {
  user_id: number
  fname: string
  mname?: string | null
  lname: string
  title?: string | null
  physician_type?: string | null
  specialty?: string | null
  introduced: boolean
  provider_since: string | null
  default: BoolNumeric
}

export type FetchPhysicianIntroductionsDto = {
  siteId: string
  patientId: number
}

export type PhysicianIntroductionsResponse = {
  physicians: PhysicianIntroduction[]
}

export type CreatePhysicianIntroductionDto = {
  siteId: string
  patientId: number
  physicianId: number
  introducedAt: string
}

export type CreatePhysicianIntroductionResponse = {
  statusCode?: number
  success?: boolean
  message?: string
  data?: unknown
}

export type CreateReferralSourceDto = {
  siteId: string
  title: string
}

export type CreateReferralSourceResponse = {
  statusCode?: number
  status?: boolean
  success?: boolean
  message?: string
  data?: unknown
}

export type Provider = {
  emr_id: number
  first_name: string
  last_name: string
}

export type EncounterPatientsResponse = {
  patients: PatientWithEncounters[]
  providers: Provider[]
  patient_statuses: PatientStatus[]
  referral_sources?: Record<string, string>
  discount_categories?: DiscountCategoryOption[]
  discount_type?: DiscountTypeOption[]
}

export type FetchEncounterPatientsDto = {
  siteId: string
}

export type DeleteProcedureDto = {
  siteId: string
  encounterUid: string
  procedureId: number
}

export type ProcedureMutationResponse = {
  added_procedures: ProcedureItem[]
  errors: string[]
  message: string
}

export type NoteKeyValue = {
  key: string
  value: string
}

export type NoteFieldMap = Record<string, string>

export type SoapNoteApiItem = {
  id: number
  date: string
  pid: number
  user: string | null
  groupname: string | null
  authorized: number
  activity: number
  subjective: string | null
  objective: string | null
  assessment: string | null
  plan: string | null
  soap_images: SoapImage[]
  subjective_labels?: NoteFieldMap | null
  subjective_templates?: NoteFieldMap | null
  objective_labels?: NoteFieldMap | null
  objective_templates?: NoteFieldMap | null
  plan_labels?: NoteFieldMap | null
  plan_templates?: NoteFieldMap | null
}

export type SoapImage = {
  id: number
  image: string
  procedure_id: number
}
export type SoapNotesApiResponse = {
  statusCode: number
  success: boolean
  data: SoapNoteApiItem[]
}

export type SoapNotePayload = {
  subjective?: string
  objective?: string
  assessment?: string
}

export type SaveSoapNoteDto = {
  patientPid: number
  encounterUid: string
  payload: SoapNotePayload
  skipESignInvalidation?: boolean
}

export type DeleteSoapNoteDto = {
  patientPid: number
  encounterUid: string
  noteId: number
}

export type SoapNoteMutationResponse = {
  statusCode: number
  success: boolean
  message?: string
  data: Partial<SoapNoteApiItem> & {
    id?: number | string
    sid?: number | string
  }
}

export type FetchSoapNotesDto = {
  patientPid: number
  encounterUid: string
}

export type EncounterPhotoApiItem = {
  id: number
  filename: string | null
  mimetype: string
  upload_date: string
  size: number
  url?: string
  data?: string
  thumbnail?: string
  note?: string | null
  category?: string | null
}

export type EncounterPhotosApiResponse = {
  statusCode: number
  success: boolean
  data: EncounterPhotoApiItem[]
}

export type FetchEncounterPhotosDto = {
  siteId: string
  patientId: number
  encounterUid: string
}

export type UploadEncounterPhotoDto = {
  siteId: string
  patientId: number
  encounterUid: string
  formData: FormData
}

export type UploadEncounterPhotoResponseData = {
  photo_id: number
  filename: string
  mimetype: string
  upload_date: string
  url: string
  thumbnail?: string
  message: string
}

export type UploadEncounterPhotoResponse = {
  statusCode: number
  success: boolean
  data: UploadEncounterPhotoResponseData
}

export type FetchPhotoStreamDto = {
  siteId: string
  patientId: number
  documentId: number
}

export type UpdateEncounterPhotoFile = {
  file_id: number
  file_name?: string
  delete?: number
  note?: string | null
}

export type UpdateEncounterPhotosDto = {
  siteId: string
  patientId: number
  encounterUid: string
  files: UpdateEncounterPhotoFile[]
}

export type UpdateEncounterPhotoResult = {
  index: number
  file_id: number
  success: boolean
  data?: {
    photo_id: number
    filename?: string
    deleted?: boolean
  }
  message: string
}

export type UpdateEncounterPhotosResponse = {
  statusCode: number
  success: boolean
  message: string
  results: UpdateEncounterPhotoResult[]
}

export type EncounterLabApiItem = {
  id: number
  filename: string
  hash: string
  mimetype: string
  docdate: string
  encounter_uuid: string | null
}

export type FetchEncounterLabsResponse = {
  data: EncounterLabApiItem[]
}

export type FetchEncounterLabsDto = {
  siteId: string
  patientId: number
  encounterUid: string
}

export type HistoricalDocument = {
  filename: string
  id: number
  mimetype: string
  size: number
  upload_date: string
}

export type FetchSoapImagesDto = {
  patientPid: number
}

export type UploadSoapImageDto = {
  patientPid: number
  encounterUid: string
  procedureId: number
  image: string
}

export type DeleteSoapImageDto = {
  patientPid: number
  encounterUid: string
  imageId: number
}

export type SoapImageApiItem = {
  id: number
  procedure_id: number
  image: string
}

export type SoapImagesApiResponse = {
  statusCode: number
  success: boolean
  data: SoapImageApiItem[]
}

export type SoapImageMutationResponse = {
  statusCode: number
  success: boolean
  data: SoapImageApiItem
}

export type ESignFormName = 'encounter' | 'photo' | 'soap_r' | 'product' | 'labs'

export type ESignLogEntry = {
  amendment: string
  datetime: string
  fname: string
  lname: string
}

export type ESignFormStatusItem = {
  form_name: ESignFormName
  is_signed: BoolNumeric
  items?: ESignLogEntry[]
  has_form_to_sign: BoolNumeric
}

export type FetchESignFormDto = {
  patientId: number
  encounterUid: string
}

export type ESignPinSettings = {
  pin_enabled: BoolNumeric
  pin_length: number
}

export type ESignFormsByName = {
  [key in ESignFormName]?: ESignFormStatusItem
}

export type ESignFormStatusData = ESignFormsByName & {
  pin_settings?: ESignPinSettings
}

export type FetchESignFormResponse = {
  statusCode: number
  success: boolean
  data: ESignFormStatusData
}

export type SubmitESignFormDto = {
  patientId: number
  encounterUid: string
  payload: {
    form_name: ESignFormName
    password: string
    amendment: string
  }
}

export type SubmitESignFormResponse = {
  statusCode: number
  success: boolean
  message?: string
}

export type FetchDocTemplateDto = {
  patientId: number
  encounterUid: string
  templateId: number
}

export type FetchDocTemplateResponseData = {
  id: number
  file_name: string
  template_content: string
  template_data?: Record<string, string>
}

export type FetchDocTemplateResponse = {
  statusCode: number
  success: boolean
  data: FetchDocTemplateResponseData
}

export type SubmitDocTemplateDto = {
  patientId: number
  templateId: number
  encounterUid: string
  payload: {
    patientSignature: string
    templateData: string
  }
}

export type SubmitDocTemplateResponse = {
  statusCode: number
  success: boolean
  message?: string
}

export type FetchSignedDocumentsDto = {
  patientId: number
}

export type SignedDocumentApiItem = {
  id: number
  filename: string
  docdate: string
  mimetype: string
  hash: string
}

export type SignedDocumentsApiResponse = {
  statusCode: number
  success: boolean
  data: SignedDocumentApiItem[]
}

export type FetchIntakeTemplatesDto = {
  patientId: number
}

export type IntakeTemplateItem = {
  id: number
  title: string
  document_type: string
}

export type IntakeTemplateGroup = {
  title: string
  templates: IntakeTemplateItem[]
}

export type IntakeTemplatesApiResponse = {
  statusCode: number
  success: boolean
  data: IntakeTemplateGroup[]
}

export type IntakeFormListItem = {
  id: number
  title: string
  document_type: string
}

export type IntakeFormsResponse = {
  statusCode: number
  success: boolean
  data: {
    forms: IntakeFormListItem[]
    short_link?: string | null
    has_profile_image?: boolean
  }
}

export type IntakeFormsData = {
  forms: IntakeFormListItem[]
  hasProfileImage: boolean
}

export type FetchIntakeFormsDto = {
  siteId: string
  patientId: number
}

export type UploadPatientProfileImageDto = {
  siteId: string
  patientId: number
  formData: FormData
}

export type UploadPatientProfileImageResponse = {
  statusCode: number
  success: boolean
}

export type FetchEncounterCheckoutDto = {
  siteId: string
  patientId: number
  encounterUid: string
}

export type EncounterCheckoutCharge = {
  billing_id: number
  code_type: string
  code: string
  modifier: string
  description: string
  units: number
  fee: number
  tax: number
  tax_rate: number
  payments: number
  balance: number
  provider_name: string
  provider_id: number
  justify: string
  is_product: boolean
  billed?: BoolNumeric
}

export type EncounterCheckoutAppliedDiscount = {
  code: string
  discount_code: string
  discount_title: string
  discount_notes: string
  amount: number
  memo: string
}

export type EncounterCheckoutSummary = {
  total_charges: number
  total_tax: number
  total_with_tax: number
  total_adjustments: number
  prev_payments: number
  prev_credits: number
  prev_gc: number
  total_payments: number
  balance: number
  has_partial_payment: boolean
  encounter_is_billed: boolean
  tax_code_text: string
}

export type EncounterCheckoutDiscountOption = {
  option_id: string
  title: string
  percent: number
}

export type EncounterCheckoutCredit = {
  credit_id: number
  source_type: string
  original_amount: number
  remaining_amount: number
  created_date: string
  note: string
  session_id: number | null
  gc_id: number | null
  gc_code: string | null
}

export type EncounterCheckoutPendingCharge = {
  amount: number
  stripe_terminal_id: number
  status: string
  message: string | null
}

export type EncounterCheckoutPerLineDiscount = {
  discount_code: string
  discount_title: string
  discount_notes: string
  amount: number
  final_amount: number
}

export type EncounterCheckoutCreditCard = {
  cc_name: string | null
  cc: string | null
  cc_expiration: string | null
  cc_cvv: string | null
  cc_zip: string | null
}

export type EncounterCheckoutData = {
  charges: EncounterCheckoutCharge[]
  applied_discounts: EncounterCheckoutAppliedDiscount[]
  summary: EncounterCheckoutSummary
  discounts: EncounterCheckoutDiscountOption[]
  credits: EncounterCheckoutCredit[]
  pending_charge?: EncounterCheckoutPendingCharge | null
  per_line_discounts?: Record<string, EncounterCheckoutPerLineDiscount>
  has_per_line_discounts?: boolean
  is_billed_snapshot?: boolean
  saved_gp_id?: number
  saved_total_amount_due?: number
  saved_total_tendered?: number
  saved_total_outstanding?: number
  saved_total_tax?: number
  patient_default_discount: CheckoutPatientDefaultDiscount | null
  credit_card?: EncounterCheckoutCreditCard | null
  stripe_key: string
}

export type EncounterCheckoutApiResponse = {
  statusCode: number
  success: boolean
  message?: string
  data: EncounterCheckoutData
}

export type FetchStripeTerminalsDto = {
  siteId: string
}

export type StripeTerminal = {
  id: number
  name: string
  // Optional for forward-compat: BE may add this when offline terminals are returned.
  // Treat missing as 'online'.
  status?: 'online' | 'offline'
}

export type FetchStripeTerminalsResponse = {
  status: boolean
  terminals: StripeTerminal[]
}

export type PayEncounterCreditPayload = {
  credit_id: number
  session_id: number | null
  gc_id: number | null
  gc_code: string | null
  amount: number
}

export type PayEncounterDiscountPayload = {
  billing_id: number
  discount_code: string
  original_amount: number
  discount_amount: number
  final_amount: number
  service_desc: string
  provider_id: number
}

export enum ChargeType {
  CreditCard = 'credit_card',
  ApplyCredit = 'apply_credit',
  Other = 'other',
}

export type PayEncounterCcPayment = {
  amount: number
  reference: string
  card_last_four: string
  card_type: string
  authorization_code: string
}

export type PayEncounterOtherPayment = {
  amount: number
  reference: string
}

export type PayEncounterPayments = {
  cc: PayEncounterCcPayment[]
  other?: PayEncounterOtherPayment
}

export type PayEncounterRequestBody = {
  stripe_terminal_id: number
  amount?: number
  encounter_tax: number
  encounter_balance: number
  prepayment_amount: number
  credits_applied: PayEncounterCreditPayload[]
  discounts_applied: PayEncounterDiscountPayload[]
  total_discount: number
  charge_type?: ChargeType
  payments?: PayEncounterPayments
}

export type PayEncounterDto = {
  siteId: string
  patientId: number
  encounterUid: string
  body: PayEncounterRequestBody
}

export type PayEncounterResponse = {
  statusCode: number
  success: boolean
  message?: string
  data?: unknown
}

export type CancelEncounterPayDto = {
  siteId: string
  patientId: number
  encounterUid: string
}

export type CancelEncounterPayResponse = {
  statusCode: number
  success: boolean
  message?: string
  data?: unknown
}

export type SaveCreditCardDto = {
  siteId: string
  patientId: number
  body: {
    cc_name: string
    cc_number: string
    cc_expiration: string
    cc_cvv: string
    cc_zip: string
  }
}

export type SaveCreditCardResponse = {
  statusCode?: number
  success?: boolean
  status?: boolean
  message?: string
  data?: unknown
}
